import { UseQueryResult } from '@tanstack/solid-query';
import { BtnClasses, IconButton, Spinner } from '@tcweb/connect-ui';
import { formatDateAndTime } from '@trimble/date-utils';
import { Component, createMemo, For, Match, Switch } from 'solid-js';
import { ThumbnailLoader } from '../thumbnail-loader/ThumbnailLoader';
import { FileDetails } from '@data-access/connect/tc-tcps';

export interface ModelVersionsProps {
  fileVersionsQuery?: UseQueryResult<FileDetails[], Error>;
  getImageBlobUrl(thumbnailUrl: string): Promise<string>;
  getFileDownloadUrl(item: FileDetails): Promise<string>;
}

export const ModelVersions: Component<ModelVersionsProps> = (props) => {
  const versions = createMemo((): FileDetails[] => {
    const query = props.fileVersionsQuery;
    if (!query || !query.isSuccess) return [];

    return query.data;
  });

  function downloadFile(item: FileDetails): void {
    props.getFileDownloadUrl(item).then((url) => {
      window.open(url, '_self');
    });
  }

  return (
    <div data-testid="model-versions" class="contents">
      <Switch>
        <Match when={props.fileVersionsQuery?.isLoading}>
          <div class="w-full h-[300px]">
            <Spinner show={true} />
          </div>
        </Match>
        <Match when={props.fileVersionsQuery?.isSuccess}>
          <ul>
            <For each={versions()}>
              {(version) => (
                <li class="flex p-2 border-b border-trimble-gray-300">
                  <div class="flex items-center mr-2">
                    <div class="w-12 h-12 border border-trimble-gray-300">
                      <ThumbnailLoader
                        thumbnailUrl={version.thumbnailUrl ?? ''}
                        altText={version.name ?? ''}
                        class="bg-gray-50"
                        imageClass="w-full h-full"
                        getThumbnailBlob={(thumbnailUrl) =>
                          props.getImageBlobUrl(thumbnailUrl)
                        }
                      />
                    </div>
                  </div>
                  <div class="flex flex-1 flex-col overflow-hidden">
                    <div class="flex flex-row leading-tight items-center">
                      <span class="font-semibold">
                        V.{version.revision ?? ''}
                      </span>
                      <span class="m-1">•</span>
                      <span class="truncate">{version.name}</span>
                    </div>
                    <div class="flex flex-col text-xs text-gray-6">
                      <span>
                        {version.createdBy?.firstName}{' '}
                        {version.createdBy?.lastName}
                      </span>
                      <span>
                        {version.createdOn
                          ? formatDateAndTime(version.createdOn)
                          : ''}
                      </span>
                    </div>
                  </div>
                  <div class="flex items-center">
                    <IconButton
                      color={BtnClasses.TERTIARY}
                      icon="tc-icon-file-download"
                      size="icon-small"
                      data-cy="model-history-download"
                      clicked={() => {
                        downloadFile(version);
                      }}
                    />
                  </div>
                </li>
              )}
            </For>
          </ul>
        </Match>
      </Switch>
    </div>
  );
};
